import {
  BoxGeometry,
  Color,
  CylinderGeometry,
  DodecahedronGeometry,
  DoubleSide,
  Group,
  InstancedMesh,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  PlaneGeometry,
  Quaternion,
  Vector3,
} from 'three';
import { Prop } from '../../../entities/Prop.js';
import { GROUP, MASK } from '../../../physics/groups.js';
import { createRandom } from '../../../utils/random.js';
import { createUTurnTexture } from './theme.js';
import { addStaticCollider } from './util.js';
import { CROSS_STREETS, MEDIAN_OPENINGS, ROAD } from './route.js';

const UP = new Vector3(0, 1, 0);

/**
 * Builds the street surface, median, lane paint, cross streets and the two
 * terminal junctions. Everything is at 1:1 world scale (metres).
 */
export function buildRoad(level, route, materials) {
  const root = level.root;
  const random = createRandom(84);

  // A single very large collider keeps vehicle suspension on a perfectly flat
  // plane while the visible ground fades into the haze. The visual ground is
  // drawn first and writes no depth: a 9 km quad and the road ribbon only a
  // couple of centimetres above it are too close together for the depth buffer
  // at this scale, and the dirt would otherwise win over the asphalt.
  const groundMaterial = materials.dirtGround.clone();
  groundMaterial.depthWrite = false;
  const ground = new Mesh(new BoxGeometry(9000, 1, 9000), groundMaterial);
  ground.receiveShadow = true;
  ground.renderOrder = -0.5;
  ground.position.set(0, -0.5, 0);
  root.add(ground);

  // The physics ground is an infinite plane rather than the visual 9 km box:
  // very large boxes lose too much precision this far from the world origin,
  // which makes suspension raycasts miss or hit phantom surfaces.
  const groundProp = new Prop({
    physics: level.world.physics,
    mesh: null,
    shape: { type: 'plane', normal: [0, 1, 0], constant: 0 },
    mass: 0,
    position: [0, 0, 0],
    friction: 1,
    restitution: 0,
    group: GROUP.LEVEL,
    mask: MASK.LEVEL,
    userData: { surface: 'dirt' },
  });
  level.register(groundProp);

  const asphalt = new Mesh(
    route.ribbonGeometry({ width: ROAD.width, y: 0.02, uvScale: 7 }),
    materials.asphalt,
  );
  asphalt.receiveShadow = true;
  root.add(asphalt);

  for (const side of [-1, 1]) {
    const sidewalk = new Mesh(
      route.ribbonGeometry({
        width: ROAD.sidewalkWidth,
        offset: side * ROAD.sidewalkOffset,
        y: 0.055,
        uvScale: 4,
      }),
      materials.concrete,
    );
    sidewalk.receiveShadow = true;
    root.add(sidewalk);

    const curb = new Mesh(
      route.ribbonGeometry({
        width: 0.3,
        offset: side * ROAD.curbOffset,
        y: 0.068,
        uvScale: 4,
      }),
      materials.curb,
    );
    curb.receiveShadow = true;
    root.add(curb);
  }

  // Continuous edge lines and the white lines beside the median.
  for (const offset of [-10.25, 10.25, -2.55, 2.55]) {
    const line = new Mesh(
      route.ribbonGeometry({ width: 0.14, offset, y: 0.031, uvScale: 4 }),
      materials.paint,
    );
    line.receiveShadow = true;
    root.add(line);
  }

  // Broken lane divider in each carriageway.
  const dashGeometry = new BoxGeometry(0.14, 0.012, 2.9);
  const dashSpacing = 9.5;
  const dashCount = Math.floor(route.length / dashSpacing) * 2;
  const dashes = new InstancedMesh(dashGeometry, materials.paint, dashCount);
  dashes.receiveShadow = true;
  const matrix = new Matrix4();
  const quaternion = new Quaternion();
  const position = new Vector3();
  const scale = new Vector3(1, 1, 1);
  let dashIndex = 0;
  for (let s = 4; s < route.length - 4 && dashIndex < dashCount; s += dashSpacing) {
    for (const offset of [-6.45, 6.45]) {
      const frame = route.frameAt(s);
      position.copy(frame.position).addScaledVector(frame.right, offset);
      position.y = 0.034;
      quaternion.setFromAxisAngle(UP, frame.yaw);
      matrix.compose(position, quaternion, scale);
      dashes.setMatrixAt(dashIndex, matrix);
      dashIndex += 1;
    }
  }
  dashes.count = dashIndex;
  dashes.instanceMatrix.needsUpdate = true;
  root.add(dashes);

  addRoadRepairs(root, route, random);
  addManholes(root, route, materials, random);

  const medianRuns = buildMedian(level, route, materials, random, root);
  addUTurnArrows(root, route);
  addCrossStreets(root, route, materials);
  addTerminalJunctions(root, route, materials);

  return { ground: groundProp, medianRuns };
}

function buildMedian(level, route, materials, random, root) {
  const runs = [];
  let cursor = 26;
  for (const opening of [...MEDIAN_OPENINGS].sort((a, b) => a.s - b.s)) {
    const start = opening.s - opening.width / 2;
    if (start - cursor > 8) runs.push([cursor, start]);
    cursor = opening.s + opening.width / 2;
  }
  if (route.length - 20 - cursor > 8) runs.push([cursor, route.length - 20]);

  const bushPositions = [];
  for (const [start, end] of runs) {
    const median = new Mesh(
      route.ribbonGeometry({
        start,
        end,
        width: ROAD.medianWidth,
        y: 0.1,
        uvScale: 3,
      }),
      materials.dirt,
    );
    median.receiveShadow = true;
    root.add(median);

    for (const side of [-1, 1]) {
      const curb = new Mesh(
        route.ribbonGeometry({
          start,
          end,
          width: 0.28,
          offset: side * (ROAD.medianWidth / 2 - 0.05),
          y: 0.14,
          uvScale: 4,
        }),
        materials.curb,
      );
      curb.receiveShadow = true;
      root.add(curb);
    }

    for (let s = start + 8; s < end - 8; s += 48) {
      const frame = route.offsetFrame(s, random.range(-0.8, 0.8));
      addStaticCollider(level, {
        x: frame.position.x,
        y: 0.12,
        z: frame.position.z,
        width: ROAD.medianWidth,
        height: 0.24,
        depth: Math.min(50, end - s + 6),
        yaw: frame.yaw,
      });
    }

    for (let s = start + 5; s < end - 5; s += random.range(11, 22)) {
      const frame = route.offsetFrame(s, random.range(-1.1, 1.1));
      bushPositions.push({
        x: frame.position.x,
        z: frame.position.z,
        scale: random.range(0.35, 0.9),
      });
    }
  }

  const bushGeometry = new DodecahedronGeometry(1, 0);
  const bushMaterial = new MeshStandardMaterial({
    color: 0xffffff,
    roughness: 1,
    flatShading: true,
  });
  const bushes = new InstancedMesh(bushGeometry, bushMaterial, bushPositions.length);
  const matrix = new Matrix4();
  const quaternion = new Quaternion();
  const color = new Color();
  const greens = [0x5f7045, 0x6c7a4e, 0x7c8357, 0x545f3d];
  bushPositions.forEach((bush, index) => {
    quaternion.setFromAxisAngle(UP, random() * Math.PI * 2);
    matrix.compose(
      new Vector3(bush.x, 0.26 + bush.scale * 0.18, bush.z),
      quaternion,
      new Vector3(bush.scale * 1.4, bush.scale, bush.scale * 1.25),
    );
    bushes.setMatrixAt(index, matrix);
    color.setHex(greens[index % greens.length]);
    bushes.setColorAt(index, color);
  });
  bushes.instanceMatrix.needsUpdate = true;
  if (bushes.instanceColor) bushes.instanceColor.needsUpdate = true;
  bushes.castShadow = true;
  bushes.receiveShadow = true;
  root.add(bushes);

  return runs;
}

function addRoadRepairs(root, route, random) {
  const geometry = new BoxGeometry(1, 0.012, 1);
  const material = new MeshStandardMaterial({
    color: 0xffffff,
    roughness: 0.98,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -1,
  });
  const count = 54;
  const mesh = new InstancedMesh(geometry, material, count);
  const matrix = new Matrix4();
  const quaternion = new Quaternion();
  const position = new Vector3();
  const scale = new Vector3();
  const color = new Color();

  for (let i = 0; i < count; i += 1) {
    const s = random.range(30, route.length - 30);
    const frame = route.frameAt(s);
    position.copy(frame.position).addScaledVector(frame.right, random.range(-9.5, 9.5));
    position.y = 0.028;
    quaternion.setFromAxisAngle(UP, frame.yaw + random.range(-0.08, 0.08));
    scale.set(random.range(1.2, 5.5), 1, random.range(2.5, 8));
    matrix.compose(position, quaternion, scale);
    mesh.setMatrixAt(i, matrix);
    const shade = random.range(0.62, 1.15);
    color.setRGB(0.19 * shade, 0.18 * shade, 0.17 * shade);
    mesh.setColorAt(i, color);
  }
  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  mesh.receiveShadow = true;
  root.add(mesh);
}

function addManholes(root, route, materials, random) {
  const geometry = new CylinderGeometry(0.34, 0.34, 0.014, 14);
  const mesh = new InstancedMesh(geometry, materials.metal, 26);
  const matrix = new Matrix4();
  const quaternion = new Quaternion();
  const position = new Vector3();
  const scale = new Vector3(1, 1, 1);
  for (let i = 0; i < 26; i += 1) {
    const s = random.range(40, route.length - 40);
    const frame = route.frameAt(s);
    position.copy(frame.position).addScaledVector(frame.right, random.range(-9.2, 9.2));
    position.y = 0.032;
    matrix.compose(position, quaternion, scale);
    mesh.setMatrixAt(i, matrix);
  }
  mesh.instanceMatrix.needsUpdate = true;
  mesh.receiveShadow = true;
  root.add(mesh);
}

function addUTurnArrows(root, route) {
  const texture = createUTurnTexture();
  const material = new MeshBasicMaterial({
    map: texture,
    transparent: true,
    depthWrite: false,
    side: DoubleSide,
    toneMapped: false,
  });
  const geometry = new PlaneGeometry(4.4, 4.4);

  for (const opening of MEDIAN_OPENINGS) {
    const variants = [
      { s: opening.s - opening.width / 2 - 8, offset: 6.3, yaw: 0 },
      { s: opening.s + opening.width / 2 + 8, offset: -6.3, yaw: Math.PI },
    ];
    for (const variant of variants) {
      const frame = route.offsetFrame(variant.s, variant.offset);
      const pivot = new Group();
      pivot.position.copy(frame.position);
      pivot.position.y = 0.037;
      pivot.rotation.y = frame.yaw + variant.yaw;
      const arrow = new Mesh(geometry, material);
      arrow.rotation.x = -Math.PI / 2;
      arrow.renderOrder = 2;
      pivot.add(arrow);
      root.add(pivot);
    }
  }
}

function addCrossStreets(root, route, materials) {
  const geometry = new BoxGeometry(200, 0.012, 7.2);
  for (const s of CROSS_STREETS) {
    const frame = route.frameAt(s);
    for (const side of [-1, 1]) {
      const mesh = new Mesh(geometry, materials.dirtStreet);
      mesh.position.copy(frame.position).addScaledVector(frame.right, side * 110);
      mesh.position.y = 0.008;
      mesh.rotation.y = frame.yaw;
      mesh.receiveShadow = true;
      root.add(mesh);

      // A small dirt apron cuts the kerb at each junction.
      const apron = new Mesh(new BoxGeometry(3.5, 0.01, 7.2), materials.dirt);
      apron.position.copy(frame.position).addScaledVector(frame.right, side * 12.6);
      apron.position.y = 0.062;
      apron.rotation.y = frame.yaw;
      apron.receiveShadow = true;
      root.add(apron);
    }
  }
}

function addTerminalJunctions(root, route, materials) {
  // Wad Madani highway, south. The player spawns just beyond it.
  const south = route.frameAt(4);
  const highway = new Mesh(new BoxGeometry(980, 0.012, 28), materials.asphaltWide);
  highway.position.copy(south.position);
  highway.position.y = 0.014;
  highway.rotation.y = south.yaw;
  highway.receiveShadow = true;
  root.add(highway);

  const highwayDashGeometry = new BoxGeometry(3.4, 0.012, 0.15);
  const highwayDashes = new InstancedMesh(highwayDashGeometry, materials.paint, 78);
  const matrix = new Matrix4();
  const quaternion = new Quaternion();
  const position = new Vector3();
  const scale = new Vector3(1, 1, 1);
  let index = 0;
  for (let x = -480; x <= 480; x += 14) {
    for (const z of [-6.2, 6.2]) {
      if (index >= 78) break;
      position
        .copy(south.position)
        .addScaledVector(south.right, x)
        .addScaledVector(south.tangent, z);
      position.y = 0.023;
      quaternion.setFromAxisAngle(UP, south.yaw);
      matrix.compose(position, quaternion, scale);
      highwayDashes.setMatrixAt(index, matrix);
      index += 1;
    }
  }
  highwayDashes.count = index;
  highwayDashes.instanceMatrix.needsUpdate = true;
  root.add(highwayDashes);

  // Al-Mashtal Street at the north end.
  const north = route.frameAt(route.length - 8);
  const cross = new Mesh(new BoxGeometry(660, 0.012, 19), materials.asphaltWide);
  cross.position.copy(north.position);
  cross.position.y = 0.015;
  cross.rotation.y = north.yaw;
  cross.receiveShadow = true;
  root.add(cross);
}
